import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import Layout from "../components/Layout";
import GlassCard from "../components/GlassCard";
import { api } from "../lib/kk";

const MAX_ATTEMPTS = 6;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | success | error | timeout

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let cancelled = false;

    const poll = async (attempt) => {
      if (cancelled) return;
      if (attempt >= MAX_ATTEMPTS) {
        setState("timeout");
        return;
      }
      try {
        const { data } = await api.get(`/payments/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("success");
          return;
        }
        if (data.status === "expired") {
          setState("error");
          return;
        }
        setTimeout(() => poll(attempt + 1), 2000);
      } catch {
        setTimeout(() => poll(attempt + 1), 2000);
      }
    };
    poll(0);
    return () => { cancelled = true; };
  }, [sessionId]);

  const content = {
    checking: { icon: <Loader2 size={48} className="text-[#BB0000] animate-spin" />, title: "Verifying payment…", body: "Hang tight while we confirm your deposit." },
    success: { icon: <CheckCircle2 size={48} color="#4CAF50" />, title: "Payment Successful!", body: "Thank you — your deposit secures your spot. We'll be in touch shortly." },
    error: { icon: <XCircle size={48} className="text-[#BB0000]" />, title: "Payment Issue", body: "We couldn't confirm this payment. Please try again or contact us." },
    timeout: { icon: <XCircle size={48} className="text-[#BB0000]" />, title: "Still Processing", body: "Your payment is taking longer than expected. Check your email for confirmation." },
  }[state];

  return (
    <Layout>
      <section className="py-28 px-6" data-testid="payment-result">
        <div className="max-w-lg mx-auto">
          <GlassCard hover={false} className="p-12 text-center">
            <div className="flex justify-center mb-5">{content.icon}</div>
            <h1 className="kk-heading text-3xl mb-3" data-testid="payment-status-title">{content.title}</h1>
            <p className="text-[#E0E0E0] mb-8">{content.body}</p>
            <button onClick={() => navigate("/")} className="kk-btn kk-btn-primary rounded-full px-8 py-4">Back to Home</button>
          </GlassCard>
        </div>
      </section>
    </Layout>
  );
}
